import { faCalendar, faCopy } from "@fortawesome/free-solid-svg-icons";
import {
  formatAccountRepDisplayName,
  formatAccountRepPhotoFilename,
  formatPhoneNumber,
} from "./util/stringUtil";

import { ACCOUNT_REP_PHOTOS_URL } from "../constants";
import { Button } from "./design-system/button";
import { GET_SALES_REP } from "./CustomerServicePopup";
import { Loading } from "@verse_core/shared_components/base/Loading";
import { selectHospitalId } from "../../../src/redux/app";
import { useAppSelector } from "../../../src/redux/hooks";
import { useQuery } from "@apollo/client";
import { useState } from "react";

const CopyEmailButton = ({ email }: { email: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Button
      styleType="light"
      colorScheme="primary"
      size="lg"
      leftIcon={faCopy}
      onClick={handleCopy}
    >
      {copied ? "Email Copied!" : "Copy Email to Send a Message"}
    </Button>
  );
};

export const AccountRepresentative = () => {
  const hospitalId = useAppSelector(selectHospitalId);
  const {
    loading,
    error,
    data: salesRepData,
  } = useQuery(GET_SALES_REP, {
    fetchPolicy: "no-cache",
    variables: {
      hospitalId: hospitalId ?? 0,
    },
    skip: !hospitalId,
  });

  const salesRep = salesRepData?.getAccountRep;

  if (loading) return <Loading />;
  if (error || !salesRep) return null;

  const displayName = formatAccountRepDisplayName(salesRep.name);
  const firstName = salesRep.name.split(" ")[0];

  return (
    <div className="tw-flex tw-flex-col tw-gap-[48px] tw-rounded-lg tw-border tw-border-solid tw-border-gray-light-1 tw-p-[24px] lg:tw-grid lg:tw-grid-cols-2 lg:tw-gap-[24px]">
      {/* Left: title, description, schedule button */}
      <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-[24px]">
        <div className="tw-flex tw-flex-col tw-gap-[8px]">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-[16px]">
            <h2 className="tw-m-0 tw-font-degular tw-text-[28px] tw-font-medium tw-leading-none tw-tracking-[-0.28px] tw-text-blue-dark-1">
              Your account representative
            </h2>
          </div>
          <p className="tw-m-0 tw-text-[14px] tw-font-medium tw-text-gray-2">
            For recurring issues, staff training support, and providing
            feedback.
          </p>
        </div>

        {salesRep.calendarPageUrl ? (
          <a
            href={salesRep.calendarPageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-no-underline"
          >
            <Button
              styleType="light"
              colorScheme="primary"
              size="lg"
              leftIcon={faCalendar}
            >
              {`Schedule a Meeting with ${firstName}`}
            </Button>
          </a>
        ) : (
          <CopyEmailButton email={salesRep.email ?? ""} />
        )}
      </div>

      {/* Right: rep info card */}
      <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-items-center tw-gap-[16px] tw-rounded-lg tw-bg-gray-light-3 tw-px-[32px] tw-py-[16px] sm:tw-flex-row sm:tw-items-center">
        <img
          className="tw-h-[120px] tw-w-[120px] tw-flex-shrink-0 tw-rounded-full tw-object-cover"
          src={`${ACCOUNT_REP_PHOTOS_URL}/${formatAccountRepPhotoFilename(salesRep.name)}`}
          alt={displayName}
        />
        <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[8px] sm:tw-w-auto">
          <p className="tw-m-0 tw-text-[18px] tw-font-bold tw-text-blue-dark-1">
            {salesRep.name}
          </p>
          <p className="tw-m-0 tw-text-[14px] tw-font-medium tw-text-gray-0">
            {salesRep.customerSuccessTeam
              ? "Customer Success Manager"
              : "Account Representative"}
          </p>
          <div className="tw-flex tw-items-center tw-gap-[8px] tw-text-[14px]">
            <i className="fas fa-envelope tw-text-gray-4" />
            <p className="tw-m-0 tw-font-medium tw-text-gray-0">
              {salesRep.email}
            </p>
          </div>
          {salesRep.phone && (
            <div className="tw-flex tw-items-center tw-gap-[8px] tw-text-[14px]">
              <i className="fas fa-phone tw-text-gray-4" />
              <p className="tw-m-0 tw-font-medium tw-text-gray-3">
                {formatPhoneNumber(salesRep.phone)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
